/**
 * Gateway Date Parsing Utilities
 *
 * Utilities for parsing date strings from gateway/task API responses.
 * Handles ISO 8601 datetime strings with optional time components.
 *
 * @example
 * // Parse ISO datetime string
 * const date = parseExistingGatewayItemDate("2023-12-25T14:30:00");
 * // Returns: Date object with hours set to 14:30:00
 *
 * @example
 * // Parse date-only string
 * const date = parseExistingGatewayItemDate("2023-12-25");
 * // Returns: Date object at midnight
 */

import { stringToDate } from "./stringToDate";

/**
 * Parses a gateway/task date string into a Date object.
 * Handles ISO 8601 format with optional time component (YYYY-MM-DDTHH:mm:ss).
 * Falls back to standard date parsing if format is not recognized.
 *
 * @param dateString - ISO 8601 datetime string (e.g., "2023-12-25T14:30:00")
 * @returns Date object with time set from the string, or midnight if no time provided
 */
export const parseExistingGatewayItemDate = (dateString: string): Date => {
  try {
    const splitDateTime = dateString.split("T");

    if (splitDateTime.length === 2) {
      const parsedDate = stringToDate(dateString);

      const parsedTime = splitDateTime[1].split(":");
      if (parsedTime.length === 3) {
        parsedDate.setHours(
          parseInt(parsedTime[0]),
          parseInt(parsedTime[1]),
          parseInt(parsedTime[2])
        );
      }

      return parsedDate;
    } else {
      return stringToDate(dateString);
    }
  } catch {
    return stringToDate(dateString);
  }
};
